import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { getNews } from "../lib/api";
import { NEWS_FALLBACK } from "../lib/api";
import { Seo } from "../components/Seo";
import { ArrowRight } from "lucide-react";

const formatDate = (iso) => {
  try {
    return new Date(iso).toLocaleDateString("tr-TR", { day: "numeric", month: "long", year: "numeric", hour: "2-digit", minute: "2-digit" });
  } catch { return ""; }
};

const NewsList = () => {
  const [news, setNews] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => { getNews(40).then((d) => setNews(d.news)).finally(() => setLoading(false)); }, []);

  const lead = news[0];
  const rest = news.slice(1);

  return (
    <div className="paper-texture">
      <Seo title="Borsa Haberleri | Güncel BIST & Piyasa Gündemi"
        description="Güncel borsa ve piyasa haberleri her gün otomatik olarak derlenip özetlenir. BIST, dolar, altın, faiz ve ekonomi gündemi."
        path="/borsa-haberleri" />
      <div className="mx-auto max-w-7xl px-4 sm:px-6 py-12">
        <div className="border-b-2 border-ink pb-6">
          <span className="kicker text-claret">Otomatik Güncellenir · Günde 2 Kez</span>
          <h1 className="mt-2 font-display text-4xl font-semibold tracking-tight text-ink sm:text-5xl">Borsa & Piyasa Haberleri</h1>
          <p className="mt-3 max-w-2xl font-display text-lg text-muted-foreground">
            Güncel borsa haberleri her gün sabah ve akşam güvenilir kaynaklardan otomatik olarak çekilip yapay zeka ile özetlenerek yayınlanır.
          </p>
        </div>

        {loading ? (
          <div className="mt-12 text-center text-muted-foreground" data-testid="news-loading">Haberler yükleniyor...</div>
        ) : news.length === 0 ? (
          <div className="mt-12 border border-dashed border-hair p-12 text-center text-muted-foreground" data-testid="news-list-empty">
            Henüz haber eklenmedi. Otomatik haber sistemi çalıştığında güncel borsa haberleri burada listelenecek.
          </div>
        ) : (
          <>
            {lead && (
              <Link to={`/borsa-haberleri/${lead.id}`} className="group mt-8 grid border border-ink lg:grid-cols-2" data-testid={`news-list-card-${lead.id}`}>
                <div className="overflow-hidden">
                  <img src={lead.image_url} alt={lead.title} referrerPolicy="no-referrer" onError={(e) => { if (e.currentTarget.src !== NEWS_FALLBACK) e.currentTarget.src = NEWS_FALLBACK; }} className="aspect-[16/10] w-full object-cover saturate-[0.9] transition duration-700 group-hover:scale-[1.03] lg:h-full" />
                </div>
                <div className="flex flex-col justify-center p-6 sm:p-10">
                  <span className="kicker text-claret">Manşet · {lead.source}</span>
                  <h2 className="mt-3 font-display text-2xl font-semibold leading-snug text-ink transition-colors group-hover:text-claret sm:text-3xl">{lead.title}</h2>
                  <p className="mt-3 text-sm leading-relaxed text-muted-foreground line-clamp-3">{lead.summary}</p>
                  <span className="mt-4 block text-xs text-muted-foreground">{formatDate(lead.published_at)}</span>
                </div>
              </Link>
            )}

            <div className="mt-10 grid gap-x-10 gap-y-10 sm:grid-cols-2 lg:grid-cols-3">
              {rest.map((n) => (
                <Link key={n.id} to={`/borsa-haberleri/${n.id}`} className="group flex flex-col border-t-2 border-ink pt-4" data-testid={`news-list-card-${n.id}`}>
                  <div className="overflow-hidden border border-hair">
                    <img src={n.image_url} alt={n.title} loading="lazy" referrerPolicy="no-referrer" onError={(e) => { if (e.currentTarget.src !== NEWS_FALLBACK) e.currentTarget.src = NEWS_FALLBACK; }} className="aspect-[16/10] w-full object-cover saturate-[0.9] transition duration-700 group-hover:scale-105" />
                  </div>
                  <span className="kicker mt-3 text-claret">{n.source}</span>
                  <h3 className="mt-1.5 font-display text-xl font-semibold leading-snug text-ink transition-colors group-hover:text-claret">{n.title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-muted-foreground line-clamp-2">{n.summary}</p>
                  <span className="mt-auto flex items-center gap-1.5 pt-3 text-xs text-muted-foreground">{formatDate(n.published_at)} <ArrowRight size={12} className="text-claret" /></span>
                </Link>
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default NewsList;
